import React from 'react';
import { useSummarizer } from '@core/store/useSummarizer';
import { SOURCE_TYPES } from '../../../../constants/summarizer';
import { SummaryOptionsBar, ProcessingButton } from '@components/ui';

interface TextConfig {
  enabled: boolean;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  rows?: number;
  helpText?: string;
  showHelpText?: boolean;
}

interface TextInputProps {
  config: TextConfig;
  disabled?: boolean;
  className?: string;
  primaryColor?: string;
  submitButtonText?: string;
  showSummaryOptions?: boolean;
  showSummaryType?: boolean;
  showTone?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  config,
  disabled = false,
  className = '',
  primaryColor: _,
  submitButtonText = 'Generate Summary',
  showSummaryOptions = true,
  showSummaryType = true,
  showTone = true,
}) => {
  const {
    textInput,
    textError,
    summaryType,
    tone,
    setTextInput,
    setSummaryType,
    setTone,
    summarize,
    isLoading: storeLoading,
    setError,
    clearError,
  } = useSummarizer();

  const maxLength = config.maxLength || 10000;
  const minLength = config.minLength || 50;
  const rows = config.rows || 8;

  const handleSubmit = async () => {
    if (!textInput.trim()) return;

    // Clear any existing errors first
    clearError();

    // Validate text length
    if (textInput.length < minLength) {
      setError(
        `Text is too short. Please enter at least ${minLength} characters (currently ${textInput.length} characters).`
      );
      return;
    } else if (textInput.length > maxLength) {
      setError(
        `Text is too long. Please reduce to ${maxLength} characters or less (currently ${textInput.length} characters).`
      );
      return;
    }

    try {
      await summarize({
        text: textInput.trim(),
        source: SOURCE_TYPES.TEXT,
        summaryType,
        tone,
      });
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey && !storeLoading && !disabled && textInput.trim()) {
      handleSubmit();
    }
  };

  const characterCount = textInput.length;
  const isValidLength = characterCount >= minLength && characterCount <= maxLength;
  const canSubmit = textInput.trim() && isValidLength && !textError && !storeLoading && !disabled;
  const loading = storeLoading;

  return (
    <div className={`text-input space-y-3 ${className}`}>
      {/* Enhanced Text Area Container */}
      <div
        className={`relative border rounded-lg overflow-hidden focus-within:ring-2 transition-all ${
          textError
            ? 'border-red-400 focus-within:ring-red-500 bg-red-50'
            : 'border-gray-300 focus-within:ring-blue-500'
        }`}
      >
        <textarea
          value={textInput}
          onChange={(e) => {
            setTextInput(e.target.value);
            if (textError) clearError(); // Clear error when user starts typing
          }}
          onKeyDown={handleKeyPress}
          placeholder={config.placeholder || 'Enter text to summarize...'}
          disabled={loading}
          rows={rows}
          className={`w-full !text-black p-4 border-none !ring-0 outline-none active:!outline-none active:!border-none focus:!outline-none transition-colors resize-vertical ${
            textError ? 'bg-red-50' : 'bg-white'
          }`}
          style={{
            minHeight: '200px',
          }}
        />
      </div>

      {/* Error Display - Positioned immediately after textarea */}
      {textError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">Text validation failed</p>
              <div className="mt-1 text-sm text-red-700">{textError}</div>
              <div className="mt-2 text-sm text-red-600">
                <p className="font-medium">Requirements:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Minimum length: {minLength} characters</li>
                  <li>Maximum length: {maxLength} characters</li>
                  <li>Current length: {characterCount} characters</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {config.helpText && config.showHelpText && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{config.helpText}</p>
        </div>
      )}

      {/* Summary Options and Submit Button - Horizontal Layout */}
      <div className="flex flex-col sm:!flex-row gap-2 items-center justify-between">
        {/* Summary Options */}
        {showSummaryOptions && (
          <SummaryOptionsBar
            summaryType={summaryType}
            tone={tone}
            onSummaryTypeChange={setSummaryType}
            onToneChange={setTone}
            disabled={loading || disabled}
            containerClassName="justify-between sm:justify-normal w-full"
            className="flex !flex-row gap-3"
            summaryTypeProps={{ triggerClassName: 'w-full max-w-[160px] bg-white text-black' }}
            toneProps={{ triggerClassName: 'w-full max-w-[160px] bg-white text-black' }}
            showSummaryType={showSummaryType}
            showTone={showTone}
          />
        )}

        {/* Enhanced Submit Button */}
        <ProcessingButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={loading}
          submitButtonText={submitButtonText}
          className="sm:ml-auto w-full sm:w-auto sm:min-w-[180px]"
        />
      </div>
    </div>
  );
};

export default TextInput;
